export interface UndoSelectTarget {
  kind: "object" | "emitter";
  id: string;
}

interface SnapshotEntities {
  objects: unknown;
  emitters: unknown;
}

function entryJsonById(raw: unknown): Map<string, string> {
  const map = new Map<string, string>();
  if (!Array.isArray(raw)) {
    return map;
  }
  for (const entry of raw) {
    const id = (entry as Record<string, unknown>).id;
    if (typeof id === "string") {
      map.set(id, JSON.stringify(entry));
    }
  }
  return map;
}

/**
 * Ids present in `after` that are new (revived by the restore) or whose
 * serialized JSON differs from `before`, in `after` array order. Ids only in
 * `before` are excluded: they no longer exist after the restore, so they can
 * never be selected.
 */
export function changedEntityIds(before: unknown, after: unknown): string[] {
  const beforeMap = entryJsonById(before);
  const changed: string[] = [];
  if (!Array.isArray(after)) {
    return changed;
  }
  for (const entry of after) {
    const rec = entry as Record<string, unknown>;
    const id = rec.id;
    if (typeof id !== "string") {
      continue;
    }
    if (beforeMap.get(id) !== JSON.stringify(entry)) {
      changed.push(id);
    }
  }
  return changed;
}

/**
 * The entity to select after an undo/redo restore: a changed transform-parent
 * object if any (a group/orbit drag changes the whole subtree, so the parent is
 * the meaningful subject), else the first changed object, else the first
 * changed emitter. Null when only connections/globals changed, which keeps the
 * snapshot's own recorded selection.
 */
export function pickUndoSelectTarget(
  before: SnapshotEntities,
  after: SnapshotEntities,
  isTransformParentType: (type: string) => boolean,
): UndoSelectTarget | null {
  const changedObjects = changedEntityIds(before.objects, after.objects);
  const changedEmitters = changedEntityIds(before.emitters, after.emitters);
  if (changedObjects.length > 0) {
    const typeById = new Map<string, string>();
    if (Array.isArray(after.objects)) {
      for (const entry of after.objects) {
        const rec = entry as Record<string, unknown>;
        if (typeof rec.id === "string" && typeof rec.type === "string") {
          typeById.set(rec.id, rec.type);
        }
      }
    }
    const parent = changedObjects.find((id: string): boolean => isTransformParentType(typeById.get(id) ?? ""));
    return { kind: "object", id: parent ?? changedObjects[0] };
  }
  if (changedEmitters.length > 0) {
    return { kind: "emitter", id: changedEmitters[0] };
  }
  return null;
}
